// ============================================================================
// JARVISTRAVEL - CORE'S FATIGUE INDEX RATINGS (JAR-1678)
// Core's rating is the true one (Ryan, 2026-09-15). The app rates every day on
// the device straight away with its port of the formula, and asks Core about the
// days on screen. Core's answers are kept here, keyed by the day's content:
// a day Core has rated shows Core's number, and any edit changes the key, so the
// device's rating shows until Core answers again. The port and Core are pinned
// to the same reference days, so a disagreement means drift; it is reported.
// ============================================================================

import { captureMessage } from '@/app/utils/observability';
import { INDEX_FORMULA, rateDay, type DayRating, type IndexDay } from '@/app/utils/fatigueIndex';

// ponytail: a flat cap, not an LRU. A session edits far fewer days than this;
// the oldest answers are dropped first if one ever does.
const MAX_RATINGS = 500;

// The reasons already reported. Capped as well, because one part of a reason —
// the formula string — arrives from Core, so a server sending a new one per
// response would grow this set for as long as the tab is open.
const MAX_REPORTED = 200;

const ratings = new Map<string, DayRating>();
const listeners = new Set<() => void>();
const reported = new Set<string>();
let version = 0;

/** Remember that a reason has been reported, forgetting the oldest past the cap. */
function markReported(reason: string): boolean {
  if (reported.has(reason)) return false;
  reported.add(reason);
  while (reported.size > MAX_REPORTED) reported.delete(reported.values().next().value as string);
  return true;
}

/**
 * A day's cache key, read field by field.
 *
 * `JSON.stringify(day)` would have done, until a caller built the same day with
 * its keys in another order: same day, different string, a silent miss that
 * reads as "Core has not rated this yet". Every field the formula reads is
 * listed here in a fixed order, so only a real difference makes a new key.
 */
export function dayKey(day: IndexDay): string {
  // Every field the same way, the scalars included: JSON.stringify writes both
  // `undefined` and `null` as null inside an array, so a day missing its shift
  // and a day whose shift is explicitly null would otherwise share one key —
  // and the same key is the in-flight key, so a collision suppresses a request
  // as well as answering with the wrong day's rating.
  return JSON.stringify([
    day?.tz_shift_hours ?? '',
    day?.days_since_shift ?? '',
    (day?.items ?? []).map((i) => [i?.kind ?? '', i?.mode ?? '', i?.start ?? '', i?.end ?? '', i?.intensity ?? '']),
  ]);
}

/** Core's rating for exactly this day, if Core has rated it. The rating is
 *  frozen: it is shared by every consumer of that day, and one caller editing
 *  what it was handed would change what the rest of them read. */
export const coreRatingFor = (day: IndexDay): DayRating | undefined => ratings.get(dayKey(day));

/**
 * Keep Core's ratings for the days it was asked about, in the order asked.
 * Returns how many disagree with this device's own rating; each kind of
 * disagreement is reported once.
 */
export function recordCoreRatings(days: IndexDay[], rated: DayRating[], coreFormula: string): number {
  let disagreements = 0;
  let stored = 0;
  days.forEach((day, i) => {
    const core = rated[i];
    // A rating without a number is not an answer: storing it would show an
    // undefined FI, and comparing it would report drift that never happened.
    if (!core || !Number.isFinite(core.fi)) return;
    const key = dayKey(day);
    // Rating it again is only for the comparison, so a day this device cannot
    // read must not cost the rest of the batch its answers.
    let device: DayRating | undefined;
    try {
      device = rateDay(day);
    } catch (e) {
      device = undefined;
      // Not silence: a day Core rated and this device refused is drift too, and
      // it is the kind this module exists to catch. Reported once per reason,
      // beside the disagreements, so a port that stops reading a shape Core
      // accepts shows up instead of quietly losing its half of the comparison.
      const why = `refused:${coreFormula}:${(e as Error)?.message ?? 'unknown'}`;
      if (markReported(why)) {
        captureMessage('[fatigue] this device could not rate a day Core rated', 'warning', {
          coreFormula,
          deviceFormula: INDEX_FORMULA,
          error: (e as Error)?.message ?? String(e),
        });
      }
    }
    if (device && device.fi !== core.fi) {
      disagreements++;
      // Not `key`: that one is the day, and it is used again below.
      const reportKey = `${coreFormula}:${device.fi}:${core.fi}`;
      if (markReported(reportKey)) {
        captureMessage('[fatigue] Core rated a day differently from this device', 'warning', {
          coreFormula,
          deviceFormula: INDEX_FORMULA,
          device: device.fi,
          core: core.fi,
        });
      }
    }
    // The same answer again is not news. Re-storing it keeps the day fresh in
    // the cache, but nothing on screen moves, so it does not earn a bump.
    const had = ratings.get(key);
    if (!had || had.fi !== core.fi || had.band !== core.band || had.burden !== core.burden) stored++;
    ratings.delete(key);
    // A frozen COPY, to the depth the app reads. Whoever asked for this rating
    // gets the stored object itself, and `factors` is nested, so a shallow
    // freeze would leave the half the explanations read editable. Copied rather
    // than frozen in place because `core` belongs to the caller: freezing it
    // would make their own array throw on any later write, which is not this
    // module's to decide.
    ratings.set(key, Object.freeze({ ...core, factors: core.factors ? Object.freeze({ ...core.factors }) : core.factors }));
  });
  // Nothing new stored, nothing changed: a bump here re-renders every subscriber
  // through useSyncExternalStore for no new rating.
  if (stored === 0) return disagreements;
  while (ratings.size > MAX_RATINGS) ratings.delete(ratings.keys().next().value as string);
  version++;
  // One subscriber that throws must not cost the others their notification:
  // they would be left rendering yesterday's rating while the store says
  // otherwise, and the throw would surface at whoever recorded the batch as if
  // the batch had failed.
  listeners.forEach((l) => {
    try {
      l();
    } catch (e) {
      captureMessage('[fatigue] a listener threw on a Core rating update', 'warning', { error: String(e) });
    }
  });
  return disagreements;
}

export function subscribeCoreRatings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Changes whenever Core's ratings do, for useSyncExternalStore. */
export const coreRatingsVersion = (): number => version;

/** Test hook: forget every rating and report. */
export function __resetCoreRatings(): void {
  ratings.clear();
  reported.clear();
  // Listeners too: a component that subscribed in one test and was never
  // unmounted would otherwise be told about the next test's ratings.
  listeners.clear();
  version = 0;
}
